import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Button,
  VStack,
  HStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Box,
  Text,
  Image,
  Progress,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Flex,
  Icon,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  Divider,
  SimpleGrid
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiFile, FiImage, FiVideo, FiFileText, FiSave, FiX } from 'react-icons/fi';
import { ArrowBackIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreateMedia = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0] || null;

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const dropzoneBg = useColorModeValue('gray.50', 'gray.700');
  const dropzoneBorder = useColorModeValue('gray.300', 'gray.500');
  const cardBg = useColorModeValue('white', 'gray.700');

  // File State
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Dropzone Config
  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.ogg', '.avi'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxSize: 50 * 1024 * 1024, // 50MB limit
    multiple: false,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        toast({
          title: 'File rejected',
          description: 'Please check file type and size requirements',
          status: 'error',
          duration: 3000
        });
        return;
      }

      const selectedFile = acceptedFiles[0];
      setFile(selectedFile);

      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setPreviewUrl(e.target.result);
        reader.readAsDataURL(selectedFile);
      } else {
        setPreviewUrl(null);
      }
    }
  });

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return FiImage;
    if (fileType.startsWith('video/')) return FiVideo;
    if (fileType === 'application/pdf') return FiFileText;
    return FiFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = () => {
    setFile(null);
    setPreviewUrl(null);
    setUploadProgress(0);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({
        title: 'Error',
        description: 'Please select a file to upload',
        status: 'error',
        duration: 3000
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setUploadProgress(10);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/assets/upload`, {
        method: 'POST',
        body: formData,
      });

      const uploadResponse = await response.json();
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok || !uploadResponse.data) {
        throw new Error(uploadResponse.error || 'File upload failed');
      }

      const assetPayload = {
        tenant_id: tenantId,
        file_name: uploadResponse.data.file_name,
        file_path: uploadResponse.data.file_path,
        file_type: uploadResponse.data.file_type,
      };

      await apiClient(`/assets`, {
        method: 'POST',
        body: JSON.stringify(assetPayload),
        headers: { 'Content-Type': 'application/json' }
      });

      toast({
        title: 'Success',
        description: 'Media uploaded successfully',
        status: 'success',
        duration: 3000
      });

      navigate('/media');
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file',
        status: 'error',
        duration: 5000
      });
      console.error('Upload Error:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getDropzoneStyle = () => {
    let borderColor = dropzoneBorder;
    let backgroundColor = dropzoneBg;

    if (isDragActive) {
      borderColor = 'blue.400';
      backgroundColor = 'blue.50';
    } else if (isDragReject) {
      borderColor = 'red.400';
      backgroundColor = 'red.50';
    }

    return {
      borderColor,
      backgroundColor,
      borderWidth: '2px',
      borderStyle: 'dashed',
      borderRadius: 'md',
      padding: '40px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      _hover: {
        borderColor: 'blue.400',
        backgroundColor: 'blue.50'
      }
    };
  };

  return (
    <Container maxW="container.md" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/media">Media</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Upload Media</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Upload Media</Heading>
        <Button 
          as={RouterLink}
          to="/media"
          leftIcon={<ArrowBackIcon />}
          variant="outline"
        >
          Back to Media
        </Button>
      </Flex>

      <VStack spacing={6} align="stretch">
        {/* Upload Section */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardHeader>
            <Heading size="md">Select File</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              {!file ? (
                <Box {...getRootProps()} sx={getDropzoneStyle()} w="full">
                  <input {...getInputProps()} />
                  <VStack spacing={4}>
                    <Icon as={FiUpload} boxSize={12} color="blue.400" />
                    <VStack spacing={2}>
                      <Text fontSize="lg" fontWeight="medium">
                        {isDragActive ? 'Drop file here' : 'Drag & drop your file here'}
                      </Text>
                      <Text color="gray.500">
                        or click to browse files
                      </Text>
                    </VStack>
                    <Box>
                      <Text fontSize="sm" color="gray.500">
                        Supported formats: Images, Videos, PDF, Word documents
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Maximum file size: 50MB
                      </Text>
                    </Box>
                  </VStack>
                </Box>
              ) : (
                <Card w="full" borderColor={borderColor} variant="outline">
                  <CardBody>
                    <HStack spacing={4} align="start">
                      {previewUrl ? (
                        <Image
                          src={previewUrl}
                          alt="Preview"
                          boxSize="100px"
                          objectFit="cover"
                          borderRadius="md"
                          fallback={<Icon as={getFileIcon(file.type)} boxSize={10} />}
                        />
                      ) : (
                        <Flex
                          boxSize="100px"
                          bg="gray.100"
                          borderRadius="md"
                          align="center"
                          justify="center"
                        >
                          <Icon as={getFileIcon(file.type)} boxSize={10} color="gray.400" />
                        </Flex>
                      )}
                      
                      <VStack align="start" flex={1} spacing={2}>
                        <Text fontWeight="medium" noOfLines={2}>
                          {file.name}
                        </Text>
                        <HStack spacing={4}>
                          <Badge colorScheme="blue" fontSize="xs">
                            {file.type}
                          </Badge>
                          <Text fontSize="sm" color="gray.500">
                            {formatFileSize(file.size)}
                          </Text>
                        </HStack>
                        
                        {uploading && (
                          <Box w="full">
                            <Text fontSize="sm" color="blue.500" mb={2}>
                              Uploading... {uploadProgress}%
                            </Text>
                            <Progress
                              value={uploadProgress}
                              colorScheme="blue"
                              size="sm"
                              borderRadius="md"
                            />
                          </Box>
                        )}
                      </VStack>
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={removeFile}
                        isDisabled={uploading}
                        leftIcon={<FiX />}
                      >
                        Remove
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* File Requirements */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="medium">File Requirements</Text>
            <Text fontSize="sm">
              • Supported formats: JPEG, PNG, GIF, WebP, MP4, WebM, PDF, DOC, DOCX
              • Maximum file size: 50MB
              • Files are automatically optimized for web delivery
            </Text>
          </Box>
        </Alert>

        {/* Action Buttons */}
        <Flex justify="flex-end" gap={4}>
          <Button 
            variant="outline" 
            as={RouterLink}
            to="/media"
            isDisabled={uploading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={uploading}
            loadingText={`Uploading... ${uploadProgress}%`}
            isDisabled={!file}
            leftIcon={<FiSave />}
          >
            Upload Media
          </Button>
        </Flex>
      </VStack>
    </Container>
  );
};

export default CreateMedia;
